import React, { useState } from 'react';
import { 
  Bug, 
  Send, 
  AlertCircle,
  FileText,
  Layers,
  ChevronRight,
  CheckCircle2
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import axios from "axios";
import { cn } from "@/lib/utils";

const BugReportForm = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorOccurred, setErrorOccurred] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    description: '',
    severity: 'Medium'
  });
  
  const email = "support@ovalu.app";
  
  const submitViaGmail = () => {
    const subject = encodeURIComponent(`🚨 Bug Report: ${formData.title}`);
    const body = encodeURIComponent(
      `Issue Title: ${formData.title}\n` +
      `Category: ${formData.category}\n` +
      `Severity: ${formData.severity}\n\n` +
      `Detailed Description:\n${formData.description}\n\n` +
      `--- Sent via Ovalu Help Center ---`
    );
    const gmailLink = `https://mail.google.com/mail/?view=cm&fs=1&to=${email}&su=${subject}&body=${body}`;
    window.open(gmailLink, '_blank');
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!formData.title || !formData.category || !formData.description) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setLoading(true);
    setErrorOccurred(false);
    
    try {
      await axios.post('/api/support/bug-report', formData);
      setSubmitted(true);
      toast.success("Bug report submitted successfully!");
    } catch (error) {
      console.error('Error reporting bug:', error);
      setErrorOccurred(true);
      toast.error("Server submission failed. Please use the Gmail option.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Dialog open={true} onOpenChange={() => setSubmitted(false)}>
        <DialogContent className="sm:max-w-md bg-white border-pink-100 rounded-[2rem] text-center p-12">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <DialogHeader>
            <DialogTitle className="text-3xl font-bold text-gray-900 mb-2">Thank You!</DialogTitle>
            <DialogDescription className="text-lg text-gray-600">
              Your feedback is invaluable. Our technical team will review this shortly and get back to you if needed.
            </DialogDescription>
          </DialogHeader>
          <Button 
            onClick={() => setSubmitted(false)}
            className="mt-8 w-full bg-[#FF6B9D] hover:bg-[#ff5a8c] text-white rounded-full h-12 text-lg font-bold"
          >
            Close
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        {children || (
          <Button variant="outline">Report a Bug</Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl bg-white border-pink-100 rounded-[2rem] p-8">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-900 flex items-center gap-2 mb-2">
            <div className="w-10 h-10 bg-pink-100 rounded-xl flex items-center justify-center">
              <Bug className="w-6 h-6 text-[#FF6B9D]" />
            </div>
            Report a Bug
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            Encountered an issue? Describe it below and help us improve Ovalu.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6 py-4">
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Issue Title
              </label>
              <Input 
                required
                placeholder="Briefly describe the problem" 
                className="border-pink-50 focus:border-[#FF6B9D] rounded-xl h-12"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Layers className="w-4 h-4" /> Category
                </label>
                <Select 
                  required
                  onValueChange={(val) => setFormData({...formData, category: val})}
                >
                  <SelectTrigger className="border-pink-50 rounded-xl h-12">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-pink-100 rounded-xl">
                    <SelectItem value="UI/Layout">UI/Layout</SelectItem>
                    <SelectItem value="Performance">Performance</SelectItem>
                    <SelectItem value="Functionality">Functionality</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" /> Severity
                </label>
                <Select 
                  defaultValue="Medium"
                  onValueChange={(val) => setFormData({...formData, severity: val})}
                >
                  <SelectTrigger className="border-pink-50 rounded-xl h-12 focus:ring-[#FF6B9D]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-pink-100 rounded-xl">
                    <SelectItem value="Low">Low - Minor</SelectItem>
                    <SelectItem value="Medium">Medium - Regular</SelectItem>
                    <SelectItem value="High">High - Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700">Detailed Description</label>
              <Textarea 
                required
                placeholder="What happened? How can we reproduce it?" 
                className="border-pink-50 focus:border-[#FF6B9D] rounded-xl min-h-[120px] resize-none"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
              />
            </div>
          </div>

          <div className="pt-4">
            <Button 
              type="button"
              onClick={submitViaGmail}
              className="w-full h-14 bg-[#FF6B9D] hover:bg-[#ff5a8c] text-white rounded-xl text-lg font-bold flex items-center justify-center gap-3 transition-all active:scale-[0.98] shadow-lg hover:shadow-pink-100"
            >
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <span className="text-white text-sm font-black">G</span>
              </div>
              Submit via Gmail
            </Button>
            <p className="text-center text-[10px] text-gray-400 mt-4">
              Your message will open in Gmail. Please remember to click "Send" in Gmail.
            </p>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BugReportForm;
